import { Event } from "@/types/event";
import { EventFormData } from "@/components/admin/event-form/types";
import { utcToLocalInput } from "@/lib/datetime";
import { supabase } from "@/integrations/supabase/client";
import { EventGroupFormRow } from "@/lib/eventGroups";

const loadGroups = async (eventId: string, tz: string): Promise<EventGroupFormRow[]> => {
  const { data, error } = await supabase
    .from("event_groups")
    .select("id, name, start_time, end_time, capacity, host_id, secondary_host_id, meeting_venue, whatsapp_group, display_order")
    .eq("event_id", eventId)
    .order("start_time", { ascending: true })
    .order("display_order", { ascending: true });
  if (error) throw error;
  return (data ?? []).map((g) => ({
    id: g.id,
    name: g.name,
    startTime: utcToLocalInput(g.start_time, tz),
    endTime: g.end_time ? utcToLocalInput(g.end_time, tz) : "",
    capacity: g.capacity,
    hostId: g.host_id ?? "",
    secondaryHostId: g.secondary_host_id ?? "",
    meetingVenue: g.meeting_venue ?? "",
    whatsappGroup: g.whatsapp_group ?? "",
    displayOrder: g.display_order,
  }));
};

export const getEventFormInit = async (event?: Event) => {
  const tz = event?.timezone || "Europe/London";
  const ev = event as any;

  let earlyBirdMode: "inherit" | "custom" | "disabled" = "inherit";
  if (ev?.early_bird_enabled === false) earlyBirdMode = "disabled";
  else if (
    ev?.early_bird_enabled === true ||
    ev?.early_bird_days_before != null ||
    ev?.early_bird_discount_percent != null
  ) earlyBirdMode = "custom";

  const groups = event?.id ? await loadGroups(event.id, tz) : [];

  const defaultValues: Partial<EventFormData> = event ? {
    title: event.title,
    description: event.description,
    venue: event.venue,
    startTime: event.start_time ? utcToLocalInput(event.start_time, tz) : '',
    endTime: event.end_time ? utcToLocalInput(event.end_time, tz) : '',
    capacity: event.capacity,
    price: event.price,
    creditCost: event.credit_cost,
    whatsappGroup: event.whatsapp_group,
    imageUrl: event.image_url,
    hostId: event.host_id || '',
    secondaryHostId: event.secondary_host_id || '',
    agendaText: event.agenda_text || '',
    allowedBookingMethods: event.allowed_booking_methods || ['stripe', 'credits', 'unlimited'],
    requiredTags: event.required_tags || [],
    timezone: tz,
    currency: ev?.currency || 'gbp',
    earlyBirdMode,
    earlyBirdDaysBefore: ev?.early_bird_days_before ?? undefined,
    earlyBirdDiscountPercent: ev?.early_bird_discount_percent ?? undefined,
    bringFriendEligible: ev?.bring_friend_eligible === true ? "enabled" : ev?.bring_friend_eligible === false ? "disabled" : "inherit",
    groups,
    originalGroupIds: groups.map((g) => g.id!),
  } : {
    hostId: '',
    secondaryHostId: '',
    agendaText: '',
    allowedBookingMethods: ['stripe', 'credits', 'unlimited'],
    requiredTags: [],
    timezone: 'Europe/London',
    currency: 'gbp',
    earlyBirdMode: 'inherit',
    bringFriendEligible: 'inherit',
    groups: [],
    originalGroupIds: [],
  };

  return defaultValues;
};
